import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  InputAdornment,
} from '@mui/material';

import CustomerPicker from './CustomerPicker';
import ResponsiveSheet, { useSheetFullScreen } from './ResponsiveSheet';
import SheetHeader from './SheetHeader';
import { TOKENS } from '../theme';

// Spec §5 — Raffle edit sheet.
//
// Only the customer and the amount are editable; everything else is shown as
// the record of what happened. The winning number came out of the server draw
// (R52) and the time is when the payout was actually taken — both are facts
// the system recorded, not entries to correct, so letting a manager retype
// them would quietly rewrite the audit trail the photos and the draw log
// exist to protect. The server ignores winnerNumber and time in the payload
// either way. Both photos are read-only. There is no add mode.
export default function RaffleEditSheet({
  open,
  onClose,
  onSave,
  entry = {},
  index,
  customers = [],
}) {
  // Pre-fill customer from the entry's customerId.
  const initialCustomer = useMemo(
    () => customers?.find((c) => c.customerId === entry.customerId) || null,
    [customers, entry.customerId]
  );

  const [selectedCustomer, setSelectedCustomer] = useState(initialCustomer);
  const [amount, setAmount] = useState(entry.amount || '');

  useEffect(() => {
    if (initialCustomer) setSelectedCustomer(initialCustomer);
  }, [initialCustomer]);

  const canSave = Boolean(selectedCustomer && amount);

  const handleSave = () => {
    if (!canSave) return;
    const name = `${selectedCustomer.firstName || ''} ${
      selectedCustomer.lastName || ''
    }`.trim();
    onSave({
      customerId: selectedCustomer.customerId,
      name,
      amount: String(amount),
    });
  };

  const idPhotoUrl = entry?.photo?.[0]?.localURL;
  const validationPhotoUrl = entry?.files?.[0]?.localURL;
  const isPage = useSheetFullScreen();

  return (
    <ResponsiveSheet open={open} onClose={onClose}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: isPage ? '100%' : undefined,
          maxHeight: isPage ? '100%' : 'inherit',
        }}
      >
        <SheetHeader
          eyebrow="Raffle"
          eyebrowColor={TOKENS.pink700}
          title={`Edit Raffle #${entry.raffleNo ?? index ?? '—'}`}
          onClose={onClose}
        />
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <CustomerPicker
            value={selectedCustomer}
            onChange={setSelectedCustomer}
            customers={customers}
          />
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Field label="Amount $*">
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </Field>
            </Grid>
            <Grid item xs={6}>
              <Field label="Winner Number (from the draw)">
                <TextField
                  fullWidth
                  size="small"
                  value={
                    entry.winnerNumber
                      ? `${entry.winnerNumber}${
                          entry.colorName ? ` · ${entry.colorName}` : ''
                        }`
                      : '—'
                  }
                  InputProps={{ readOnly: true }}
                  disabled
                />
              </Field>
            </Grid>
          </Grid>
          <Field label="Time paid">
            <TextField
              fullWidth
              size="small"
              value={entry.time || '—'}
              InputProps={{ readOnly: true }}
              disabled
            />
          </Field>
          {(idPhotoUrl || validationPhotoUrl) && (
            <Field label="Photos (read-only — captured at counter)">
              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                <PhotoPreview
                  src={idPhotoUrl}
                  label="ID Picture"
                  borderColor={TOKENS.blue200}
                  chipBg={TOKENS.blue50}
                  chipText={TOKENS.blue700}
                />
                <PhotoPreview
                  src={validationPhotoUrl}
                  label="Validation"
                  borderColor={TOKENS.emerald200}
                  chipBg={TOKENS.emerald50}
                  chipText={TOKENS.emerald700}
                />
              </Grid>
            </Field>
          )}
        </Box>
        <Box
          sx={{
            p: 1.5,
            borderTop: `1px solid ${TOKENS.slate200}`,
            display: 'flex',
            gap: 1,
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
            background: '#fff',
            pb: isPage ? 'calc(12px + env(safe-area-inset-bottom))' : 1.5,
          }}
        >
          <Button fullWidth variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            disabled={!canSave}
            onClick={handleSave}
            sx={{
              background: TOKENS.pink600,
              '&:hover': { background: TOKENS.pink700 },
            }}
          >
            Save Changes
          </Button>
        </Box>
      </Box>
    </ResponsiveSheet>
  );
}

function Field({ label, children }) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: TOKENS.slate500,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
  );
}

function PhotoPreview({ src, label, borderColor, chipBg, chipText }) {
  return (
    <Grid item xs={6}>
      <Box
        sx={{
          border: `2px solid ${borderColor}`,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ background: chipBg, px: 1, py: 0.5 }}>
          <Typography
            sx={{
              fontSize: 9,
              fontWeight: 700,
              textTransform: 'uppercase',
              color: chipText,
            }}
          >
            {label}
          </Typography>
        </Box>
        <Box sx={{ aspectRatio: '3/4', background: TOKENS.slate100 }}>
          {src && (
            <Box
              component="img"
              src={src}
              alt={label}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          )}
        </Box>
      </Box>
    </Grid>
  );
}
